package app.footyguide.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.domain.model.Team
import app.footyguide.core.theme.Club
import app.footyguide.core.theme.ClubCatalog
import app.footyguide.core.locale.LocalStrings

fun buildDressAppCta(club: Club?): String {
    return if (club != null) {
        "Vestir FootyGuide com ${club.name}"
    } else {
        "Selecione seu clube"
    }
}

fun resolveClubInitials(slug: String): String {
    val club = ClubCatalog.bySlug(slug)
    if (club != null) return club.initials
    return slug.take(3).uppercase()
}

fun resolveClubColor(club: Club?): String {
    return club?.c1 ?: "#0B6E4F"
}

fun normalizeClubSelectionIds(rawIds: Collection<String>): List<String> {
    return rawIds
        .map { raw -> ClubCatalog.bySlug(raw)?.slug ?: raw.trim().lowercase() }
        .filter { it.isNotBlank() && it != "neutral" }
        .distinct()
        .sorted()
}

/**
 * Stitch Design System (Screen 2: Seleção de Clube / O App se Veste).
 * Pure Paper & Ink aesthetic: #FFFFFF surface, #E7E7E1 hairline border,
 * live preview card recoloring to club's primary palette, natural crests and initials.
 */
@Composable
fun TeamSelectionModal(
    serieAIds: Set<String>,
    teams: Map<String, Team>,
    currentTeamId: String? = null,
    onTeamSelected: (String) -> Unit,
    onClose: () -> Unit
) {
    val canonicalCurrentId = currentTeamId?.takeIf { it.isNotBlank() }?.let { ClubCatalog.bySlug(it)?.slug ?: it }
    var stagedTeamId by remember(canonicalCurrentId) { mutableStateOf(canonicalCurrentId) }
    val strings = LocalStrings.current
    val normalizedClubIds = remember(serieAIds) { normalizeClubSelectionIds(serieAIds) }

    DisposableEffect(Unit) {
        val bodyStyle = kotlinx.browser.document.body?.style
        val previousOverflow = bodyStyle?.getPropertyValue("overflow") ?: ""
        val previousTouchAction = bodyStyle?.getPropertyValue("touch-action") ?: ""
        bodyStyle?.setProperty("overflow", "hidden")
        bodyStyle?.setProperty("touch-action", "none")
        onDispose {
            if (previousOverflow.isNotBlank()) {
                bodyStyle?.setProperty("overflow", previousOverflow)
            } else {
                bodyStyle?.removeProperty("overflow")
            }
            if (previousTouchAction.isNotBlank()) {
                bodyStyle?.setProperty("touch-action", previousTouchAction)
            } else {
                bodyStyle?.removeProperty("touch-action")
            }
        }
    }

    Div({
        classes("fg-modal-backdrop-animated")
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(22, 24, 29, 0.45)"))
            property("backdrop-filter", "blur(8px)")
            property("-webkit-backdrop-filter", "blur(8px)")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("z-index", "2000")
            padding(16.px)
            property("touch-action", "none")
            property("overscroll-behavior", "contain")
        }
        onClick { onClose() }
    }) {
        Div({
            classes("fg-sheet-animated")
            onClick { it.stopPropagation() }
            style {
                position(Position.Relative)
                width(100.percent)
                maxWidth(520.px)
                backgroundColor(Color("#FFFFFF"))
                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                borderRadius(20.px)
                property("overflow", "hidden")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("box-shadow", "0 20px 40px rgba(22, 24, 29, 0.12)")
            }
        }) {
            // HEADER
            Div({
                style {
                    padding(16.px, 20.px, 12.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.FlexStart)
                    property("border-bottom", "1px solid #E7E7E1")
                }
            }) {
                Div({ style { flex(1) } }) {
                    H2({
                        style {
                            margin(0.px)
                            fontSize(18.px)
                            fontWeight(800)
                            fontFamily("Sora, sans-serif")
                            color(Color("#16181D"))
                            letterSpacing((-0.5).px)
                        }
                    }) {
                        Text(strings.chooseClubTitle)
                    }
                    P({
                        style {
                            margin(4.px, 0.px, 0.px)
                            fontSize(12.px)
                            fontFamily("Inter, sans-serif")
                            color(Color("#71717A"))
                            fontWeight(500)
                        }
                    }) {
                        Text(strings.chooseClubSubtitle)
                    }
                }

                Button({
                    onClick { onClose() }
                    style {
                        backgroundColor(Color("#F4F4F5"))
                        border(0.px)
                        color(Color("#16181D"))
                        cursor("pointer")
                        width(28.px)
                        height(28.px)
                        borderRadius(14.px)
                        display(DisplayStyle.Grid)
                        property("place-items", "center")
                        fontSize(14.px)
                        fontWeight(700)
                    }
                }) {
                    Text("✕")
                }
            }

            // RESPONSIVE CLUB GRID (5 columns on desktop/tablet, no vertical scroll)
            Div({
                classes("fg-team-modal-grid")
                style {
                    padding(12.px, 16.px)
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(5, 1fr)")
                    gap(8.px)
                }
            }) {
                normalizedClubIds.forEach { id ->
                    val team = teams[id]
                    val club = ClubCatalog.bySlug(id)
                    val isSelected = stagedTeamId == id
                    val clubColor = resolveClubColor(club)
                    val initials = resolveClubInitials(id)

                    Div({
                        id("team-item-$id")
                        onClick {
                            stagedTeamId = id
                            onTeamSelected(id)
                            onClose()
                        }
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            justifyContent(JustifyContent.Center)
                            alignItems(AlignItems.Center)
                            padding(8.px, 4.px)
                            borderRadius(12.px)
                            cursor("pointer")
                            backgroundColor(Color(if (isSelected) "#FFFFFF" else "#FAFAF7"))
                            border(
                                width = if (isSelected) 2.px else 1.px,
                                style = LineStyle.Solid,
                                color = Color(if (isSelected) clubColor else "#E7E7E1")
                            )
                            property("transition", "all 0.15s ease")
                            if (isSelected) {
                                property("box-shadow", "0 4px 12px rgba(22, 24, 29, 0.08)")
                            }
                        }
                    }) {
                        SafeImg(
                            src = team?.logo ?: club?.logoUrl,
                            fallbackIcon = Icons.SHIELD,
                            fallbackSize = CrestSizes.MATCH_CARD_FALLBACK,
                            loading = "eager",
                            modifier = {
                                width(CrestSizes.MATCH_CARD_PX.px)
                                height(CrestSizes.MATCH_CARD_PX.px)
                                property("width", CrestSizes.MATCH_CARD_CSS)
                                property("height", CrestSizes.MATCH_CARD_CSS)
                                property("object-fit", "contain")
                            }
                        )
                        Span({
                            style {
                                marginTop(4.px)
                                fontSize(10.px)
                                fontWeight(700)
                                fontFamily("Inter, sans-serif")
                                color(Color(if (isSelected) clubColor else "#52525B"))
                                letterSpacing(0.2.px)
                            }
                        }) {
                            Text(initials)
                        }
                    }
                }
            }

            // ACTION FOOTER: Continuar sem time do coração
            Div({
                style {
                    padding(10.px, 16.px, 14.px)
                    property("border-top", "1px solid #E7E7E1")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    backgroundColor(Color("#FFFFFF"))
                    property("border-bottom-left-radius", "20px")
                    property("border-bottom-right-radius", "20px")
                }
            }) {
                Button({
                    onClick {
                        onTeamSelected("")
                        onClose()
                    }
                    style {
                        width(100.percent)
                        padding(9.px, 14.px)
                        backgroundColor(Color("#FAFAF7"))
                        color(Color("#71717A"))
                        borderRadius(10.px)
                        border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                        fontFamily("Inter, sans-serif")
                        fontSize(12.px)
                        fontWeight(600)
                        cursor("pointer")
                        property("transition", "all 0.15s ease")
                    }
                }) {
                    Text(strings.continueWithoutTeam)
                }
            }
        }
    }
}
